import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Container,
  Paper
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import Checkout from './Checkout';
import ErrorBoundary from './ErrorBoundary';

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  productId: string;
}

interface CartProps {
  items: CartItem[];
  onRemoveItem: (itemId: string) => void;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onClearCart: () => void;
  onClearCartBadge?: () => void;
}

const Cart: React.FC<CartProps> = ({
  items,
  onRemoveItem,
  onUpdateQuantity,
  onClearCart,
  onClearCartBadge
}) => {
  const [showCheckout, setShowCheckout] = useState(false);

  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Check for clearCartOnReturn flag when component mounts
  useEffect(() => {
    const shouldClearCart = sessionStorage.getItem('clearCartOnReturn');
    if (shouldClearCart === 'true') {
      onClearCart();
      sessionStorage.removeItem('clearCartOnReturn');
    }
  }, [onClearCart]);

  const handlePaymentSuccess = (details: any) => {
    console.log('Payment successful:', details);
    // Clear cart badge immediately so it shows 0
    if (onClearCartBadge) {
      onClearCartBadge();
    }
    // Don't clear cart items yet - let user see success page and access downloads
    // Cart items will be cleared when user navigates away from success page
  };

  const handleNavigateAway = () => {
    // Clear cart when user chooses to navigate away from success page
    onClearCart();
    setShowCheckout(false);
  };

  const handlePaymentError = (error: any) => {
    console.error('Payment error:', error);
  };

  const handlePaymentCancel = () => {
    setShowCheckout(false);
  };

  const handleBackToCart = () => {
    setShowCheckout(false);
  };

  if (items.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 3 } }}>
        <Paper 
          sx={{ 
            p: { xs: 4, md: 6 }, 
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'grey.300'
          }}
        >
          <Typography variant="h5" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
            Your cart is empty
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
            Add some SVG designs to get started. Browse our collection of high-quality vector graphics!
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (showCheckout) {
    return (
      <ErrorBoundary>
        <Checkout
          items={items}
          onBackToCart={handleBackToCart}
          onPaymentSuccess={handlePaymentSuccess}
          onPaymentError={handlePaymentError}
          onPaymentCancel={handlePaymentCancel}
          onNavigateAway={handleNavigateAway}
        />
      </ErrorBoundary>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 3 } }}>
      <Paper 
        sx={{ 
          p: { xs: 3, md: 4 }, 
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: 'background.paper'
        }}
      >
        <Typography 
          variant="h4" 
          sx={{ 
            mb: { xs: 3, md: 4 }, 
            fontWeight: 700,
            textAlign: 'center',
            background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Shopping Cart ({items.length} items)
        </Typography>

        <List sx={{ mb: 3 }}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem 
                sx={{ 
                  py: { xs: 2, md: 3 },
                  px: { xs: 1, md: 2 },
                  borderRadius: 2,
                  '&:hover': {
                    bgcolor: 'grey.50'
                  }
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body1" color="text.secondary">
                        ${item.price.toFixed(2)} × {item.quantity}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ mt: 0.5 }}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => onRemoveItem(item.id)}
                    sx={{ 
                      color: 'error.main',
                      '&:hover': {
                        bgcolor: 'error.light',
                        color: 'error.dark'
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < items.length - 1 && <Divider sx={{ mx: 2 }} />}
            </React.Fragment>
          ))}
        </List>

        <Box sx={{ 
          mt: 4, 
          pt: 3, 
          borderTop: 2, 
          borderColor: 'grey.200',
          bgcolor: 'grey.50',
          borderRadius: 2,
          p: 3
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Total:
            </Typography>
            <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
              ${totalAmount.toFixed(2)}
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              onClick={onClearCart}
              size="large"
              sx={{ 
                minWidth: 140,
                py: 1.5,
                fontWeight: 600
              }}
            >
              Clear Cart
            </Button>
            <Button
              variant="contained"
              onClick={() => setShowCheckout(true)}
              size="large"
              sx={{ 
                minWidth: 180,
                py: 1.5,
                fontWeight: 600,
                background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                }
              }}
            >
              Proceed to Checkout
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Cart; 